import { useEffect, useRef, useState } from "react";

import { TICK_INTERVAL_MS } from "../lib/constants";

/**
 * Drives an animated progress bar that ticks on an interval until `completed`.
 *
 * @param completed - When true, jumps the bar to 100% and stops ticking.
 * @param computeProgress - Pure function that maps elapsed seconds → a
 *   progress percentage [0, 100]. Called on every tick.
 * @returns Current progress percentage.
 */
const useProgressBar = (
  completed: boolean,
  computeProgress: (elapsedSeconds: number) => number
): number => {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (completed) return;

    const startTime = startTimeRef.current;

    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      setProgress(computeProgress(elapsed));
    };

    tick();
    intervalRef.current = setInterval(tick, TICK_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [completed, computeProgress]);

  useEffect(() => {
    if (completed) setProgress(100);
  }, [completed]);

  return progress;
};

export default useProgressBar;
